import React, { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import { getAllTickets, updateTicketStatus } from "../../apis/adminTicket";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../components/shared/dialog";
import { X } from "lucide-react";

const TicketsList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalTickets: 0,
    itemsPerPage: 10,
  });
  const [statusFilter, setStatusFilter] = useState("");

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  useEffect(() => {
    fetchTickets();
  }, [pagination.currentPage, statusFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        status: statusFilter || undefined,
      };
      const response = await getAllTickets(params);

      // Handle different response structures if necessary
      if (response) {
        setTickets(response.tickets || []);
        setPagination((prev) => ({
          ...prev,
          currentPage: response.currentPage || prev.currentPage,
          totalPages: response.totalPages || 1,
          totalTickets: response.totalTickets || 0,
        }));
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedTicket || !newStatus) return;

    try {
      await updateTicketStatus(selectedTicket._id, newStatus);
      toast.success("Ticket status updated successfully");
      setOpenStatusDialog(false);
      fetchTickets();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update ticket status");
    }
  };

  const openUpdateDialog = (ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setOpenStatusDialog(true);
  };

  const openDetailsModal = (ticket) => {
    setSelectedTicket(ticket);
    setOpenDetailsDialog(true);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "finished":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "in progress":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "not started":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
      default:
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    }
  };

  const columns = useMemo(
    () => ["ID", "Subject / Title", "User", "Date", "Status", "Action"],
    []
  );

  return (
    <div className="page-content dark:bg-darkBg1 dark:text-[#c7d8eb]">
      <Container fluid>
        <div className="p-6 flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold dark:text-[#c7d8eb]">
              Tickets Management
            </h1>
            <div className="w-48">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                className="w-full p-2 border rounded bg-white text-gray-900 dark:bg-darkBg1 dark:text-white dark:border-gray-500"
              >
                <option value="">All Statuses</option>
                <option value="not started">Not Started</option>
                <option value="in progress">In Progress</option>
                <option value="finished">Finished</option>
              </select>
            </div>
          </div>

          <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white rounded-md">
            <div className="overflow-x-auto">
              {/* Desktop Header */}
              <div className="hidden lg:flex bg-gray-100 dark:bg-gray-800">
                {columns.map((column, index) => (
                  <div
                    key={`column-${index}`}
                    className="w-1/6 min-w-[130px] p-4 font-semibold text-center border-b border-gray-300 dark:border-gray-600 dark:text-gray-200"
                  >
                    {column}
                  </div>
                ))}
              </div>

              {/* List */}
              {loading ? (
                <div className="text-center p-4">Loading...</div>
              ) : tickets.length === 0 ? (
                <div className="text-center p-4">No tickets found.</div>
              ) : (
                tickets.map((ticket, index) => (
                  <div
                    key={ticket._id}
                    className="flex flex-col lg:flex-row border-b border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    {/* Desktop Row */}
                    <div className="hidden lg:flex w-full items-center">
                      <div className="w-1/6 min-w-[130px] p-3 text-center text-sm text-gray-600 dark:text-gray-400">
                        {ticket.ticketNumber || ticket._id.substring(ticket._id.length - 6)}
                      </div>
                      <div className="w-1/6 min-w-[130px] p-3 text-center font-medium truncate px-2" title={ticket.body}>
                        {ticket.body || ticket.title || ticket.subject || "No Content"}
                      </div>
                      <div className="w-1/6 min-w-[130px] p-3 text-center">
                        {ticket.user?.email ||
                          ticket.email ||
                          ticket.user?.name ||
                          "Unknown"}
                      </div>
                      <div className="w-1/6 min-w-[130px] p-3 text-center">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </div>
                      <div className="w-1/6 min-w-[130px] p-3 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(
                            ticket.status
                          )}`}
                        >
                          {ticket.status}
                        </span>
                      </div>
                      <div className="w-1/6 min-w-[130px] p-3 text-center">
                        <button
                          onClick={() => openUpdateDialog(ticket)}
                          className="px-3 py-1 bg-primaryColor text-white text-sm rounded hover:bg-opacity-90"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => openDetailsModal(ticket)}
                          className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-opacity-90 ml-2"
                        >
                          Details
                        </button>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden p-4 space-y-2">
                        <div className="flex justify-between">
                            <span className="font-bold">ID:</span>
                            <span className="text-sm text-gray-500">{ticket.ticketNumber || ticket._id.substring(ticket._id.length - 6)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="font-bold">Subject:</span>
                            <span className="truncate max-w-[200px]">{ticket.body || ticket.title || ticket.subject || "No Content"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="font-bold">User:</span>
                             <span className="truncate max-w-[200px]">{ticket.user?.email || ticket.email || ticket.user?.name || "Unknown"}</span>
                        </div>
                         <div className="flex justify-between">
                            <span className="font-bold">Date:</span>
                            <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                             <span className="font-bold">Status:</span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(
                                    ticket.status
                                )}`}
                                >
                                {ticket.status}
                                </span>
                        </div>
                        <div className="mt-2 text-right">
                             <button
                                onClick={() => openUpdateDialog(ticket)}
                                className="px-3 py-1 bg-primaryColor text-white text-sm rounded hover:bg-opacity-90"
                                >
                                Update Status
                            </button>
                             <button
                                onClick={() => openDetailsModal(ticket)}
                                className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-opacity-90 ml-2"
                                >
                                Details
                            </button>
                        </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
             {/* Pagination */}
            {!loading && tickets.length > 0 && (
              <div className="p-4 flex justify-between items-center border-t border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 rounded-b-md">
                 <span className="text-sm text-gray-500 dark:text-gray-400">
                    Page {pagination.currentPage} of {pagination.totalPages}
                 </span>
                 <div className="flex gap-2">
                    <button 
                        className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700 dark:border-gray-600"
                        disabled={pagination.currentPage === 1}
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                    >
                        Previous
                    </button>
                    <button 
                        className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-700 dark:border-gray-600"
                        disabled={pagination.currentPage === pagination.totalPages}
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                    >
                        Next
                    </button>
                 </div>
              </div>
            )}
          </div>
        </div>

        {/* Update Status Dialog */}
        <Dialog open={openStatusDialog} onOpenChange={setOpenStatusDialog}>
          <DialogContent className="max-w-screen w-[calc(100vw-20px)] lg:w-[500px] bg-gray-200 text-gray-900 dark:bg-[#171d37] dark:text-[#c7d8eb]">
            <DialogHeader className="flex flex-row justify-between items-center">
              <h2 className="text-xl font-bold dark:text-[#c7d8eb]">
                Update Ticket Status
              </h2>
              <X
                className="cursor-pointer dark:text-[#c7d8eb]"
                onClick={() => setOpenStatusDialog(false)}
              />
            </DialogHeader>
            <hr className="my-2 border-gray-300 dark:border-gray-600" />
            
            <div className="space-y-4 p-2">
              <div className="flex flex-col gap-4">
                 <div>
                    <label className="block text-md font-semibold text-gray-700 dark:text-[#c7d8eb] mb-2">Subject</label>
                    <div className="w-full px-4 py-3 bg-gray-50 dark:bg-darkBg1 border border-gray-400 dark:border-gray-600 rounded text-gray-900 dark:text-gray-200 max-h-40 overflow-y-auto">
                        {selectedTicket?.body || selectedTicket?.title || selectedTicket?.subject || "No Content"}
                    </div>
                 </div>
                 
                 <div>
                    <label className="block text-md font-semibold text-gray-700 dark:text-[#c7d8eb] mb-2">Current Status</label>
                    <div className="w-full px-4 py-3 bg-gray-50 dark:bg-darkBg1 border border-gray-400 dark:border-gray-600 rounded">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadgeColor(selectedTicket?.status)}`}>
                            {selectedTicket?.status}
                        </span>
                    </div>
                 </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-md font-semibold text-gray-700 dark:text-[#c7d8eb]">New Status</label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-400 dark:border-gray-600 rounded bg-gray-50 text-gray-900 dark:bg-darkBg1 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="" disabled>Select a status</option>
                      <option value="not started">Not Started</option>
                      <option value="in progress">In Progress</option>
                      <option value="finished">Finished</option>
                    </select>
                  </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
               <button 
                 onClick={() => setOpenStatusDialog(false)}
                 className="px-4 py-2 mr-2 rounded text-gray-700 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
               >
                 Cancel
               </button>
               <button 
                 onClick={handleStatusUpdate}
                 className="px-4 py-2 rounded bg-primaryColor text-white shadow hover:bg-opacity-90"
               >
                 Save Changes
               </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Container>
      
      {/* View Details Dialog */}
        <Dialog open={openDetailsDialog} onOpenChange={setOpenDetailsDialog}>
          <DialogContent className="max-w-screen w-[calc(100vw-20px)] lg:w-[600px] bg-gray-200 text-gray-900 dark:bg-[#171d37] dark:text-[#c7d8eb]">
            <DialogHeader className="flex flex-row justify-between items-center">
              <h2 className="text-xl font-bold dark:text-[#c7d8eb]">
                Ticket Details
              </h2>
              <X
                className="cursor-pointer dark:text-[#c7d8eb]"
                onClick={() => setOpenDetailsDialog(false)}
              />
            </DialogHeader>
            <hr className="my-2 border-gray-300 dark:border-gray-600" />

            <div className="space-y-4 p-2 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                        <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400">Status</label>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadgeColor(selectedTicket?.status)}`}>
                            {selectedTicket?.status}
                        </span>
                     </div>
                     <div>
                        <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400">Created At</label>
                        <div className="text-gray-900 dark:text-gray-200 font-medium">
                           {selectedTicket?.createdAt ? new Date(selectedTicket.createdAt).toLocaleString() : "N/A"}
                        </div>
                     </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400">User Details</label>
                    <div className="mt-1 p-3 bg-gray-50 dark:bg-darkBg1 border border-gray-400 dark:border-gray-600 rounded">
                        <p><span className="font-semibold">Name:</span> {selectedTicket?.user?.firstName} {selectedTicket?.user?.lastName}</p>
                        <p><span className="font-semibold">Email:</span> {selectedTicket?.user?.email}</p>
                         <p><span className="font-semibold">ID:</span> {selectedTicket?.user?._id}</p>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-500 dark:text-gray-400">Ticket ID</label>
                    <div className="text-gray-900 dark:text-gray-200 font-medium">
                        {selectedTicket?.ticketNumber || selectedTicket?._id}
                    </div>
                </div>

                 <div>
                    <label className="block text-md font-semibold text-gray-700 dark:text-[#c7d8eb] mb-2">Subject / Body</label>
                     <div className="w-full px-4 py-3 bg-gray-50 dark:bg-darkBg1 border border-gray-400 dark:border-gray-600 rounded text-gray-900 dark:text-gray-200 whitespace-pre-wrap">
                        {selectedTicket?.body || selectedTicket?.title || selectedTicket?.subject || "No Content"}
                    </div>
                 </div>
            </div>

            <DialogFooter className="mt-4">
               <button 
                 onClick={() => setOpenDetailsDialog(false)}
                 className="px-4 py-2 rounded text-gray-700 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
               >
                 Close
               </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
  
    </div>
  );
};

export default TicketsList;
